import { api } from '../../../servicesGlobais/api'
import { Usuario, UsuarioFormData, UsuarioFilters, PapelDisponivel, ClockifyUsuario } from '../tipos/usuario'
import { Pessoa } from '../../../tiposGlobais/pessoa'

// Removido - agora os papéis são buscados do backend

function normalizeUsuario(backendUsuario: any): Usuario {
  if (!backendUsuario) {
    return {
      id: '',
      nome: '',
      email: '',
      ativo: true,
      pessoaId: 0,
      pessoaNome: '',
      papelCodigo: undefined,
      papeisCodigos: [],
      papeisNomes: [],
      dataCriacao: undefined,
    }
  }

  const id =
    backendUsuario.id ??
    backendUsuario.Id ??
    backendUsuario.usuarioId ??
    backendUsuario.UsuarioId

  const papeisCodigos =
    backendUsuario.papeisCodigos ??
    backendUsuario.PapeisCodigos ??
    []

  const papeisNomes =
    backendUsuario.papeisNomes ??
    backendUsuario.PapeisNomes ??
    []

  return {
    id: id ? id.toString() : undefined,
    nome: backendUsuario.nome ?? backendUsuario.Nome ?? '',
    email: backendUsuario.email ?? backendUsuario.Email ?? '',
    ativo: backendUsuario.ativo ?? backendUsuario.Ativo ?? true,
    pessoaId:
      backendUsuario.pessoaId ??
      backendUsuario.PessoaId ??
      0,
    pessoaNome:
      backendUsuario.pessoaNome ??
      backendUsuario.PessoaNome ??
      '',
    dataCriacao:
      backendUsuario.dataCriacao ??
      backendUsuario.DataCriacao ??
      undefined,
    papelCodigo: Array.isArray(papeisCodigos) && papeisCodigos.length > 0
      ? Number(papeisCodigos[0])
      : undefined,
    papeisCodigos: Array.isArray(papeisCodigos)
      ? papeisCodigos.map((codigo: any) => Number(codigo))
      : [],
    papeisNomes: Array.isArray(papeisNomes)
      ? papeisNomes.map((nome: any) => String(nome))
      : [],
    fotoUrl: backendUsuario.fotoUrl ?? backendUsuario.FotoUrl,
    userIdClockify: backendUsuario.userIdClockify ?? backendUsuario.UserIdClockify,
  }
}

function convertToFormData(backendUsuario: any): UsuarioFormData {
  const normalized = normalizeUsuario(backendUsuario)

  return {
    id: normalized.id,
    nome: normalized.nome,
    email: normalized.email,
    senha: '',
    confirmarSenha: '',
    ativo: normalized.ativo,
    pessoaId: normalized.pessoaId ? String(normalized.pessoaId) : '',
    papelCodigo: normalized.papelCodigo ? String(normalized.papelCodigo) : '',
    userIdClockify: normalized.userIdClockify,
  }
}

function convertToBackend(usuario: UsuarioFormData) {
  const idLimpo =
    usuario.id && usuario.id.toString().trim() !== '' ? usuario.id : null
  return {
    id: idLimpo,
    nome: usuario.nome,
    email: usuario.email,
    senha: usuario.senha && usuario.senha.trim() !== '' ? usuario.senha : undefined,
    ativo: usuario.ativo,
    pessoaId: usuario.pessoaId ? Number(usuario.pessoaId) : 0,
    papeisCodigos: usuario.papelCodigo ? [Number(usuario.papelCodigo)] : [],
    userIdClockify: usuario.userIdClockify || undefined,
  }
}

function filtrarUsuariosLocalmente(usuarios: Usuario[], filters?: UsuarioFilters): Usuario[] {
  if (!filters) return usuarios

  let resultado = [...usuarios]

  if (filters.search && filters.search.trim()) {
    const termo = filters.search.trim().toLowerCase()
    resultado = resultado.filter((usuario) => {
      return (
        usuario.nome.toLowerCase().includes(termo) ||
        usuario.email.toLowerCase().includes(termo) ||
        (usuario.pessoaNome || '').toLowerCase().includes(termo)
      )
    })
  }

  if (filters.ativo && filters.ativo !== 'todos') {
    const ativo = filters.ativo === 'sim'
    resultado = resultado.filter((usuario) => usuario.ativo === ativo)
  }

  if (filters.papelCodigo && filters.papelCodigo !== 'todos') {
    resultado = resultado.filter((usuario) =>
      usuario.papelCodigo !== undefined && String(usuario.papelCodigo) === filters.papelCodigo
    )
  }

  return resultado
}

export const usuarioService = {
  async listarUsuarios(filters?: UsuarioFilters): Promise<Usuario[]> {
    const usuarios = await api.get<any[]>('/usuario')
    const normalizados = Array.isArray(usuarios) ? usuarios.map(normalizeUsuario) : []
    return filtrarUsuariosLocalmente(normalizados, filters)
  },

  async buscarUsuariosClockify(email: string): Promise<ClockifyUsuario[]> {
    const usuarios = await api.get<any[]>(`/usuario/clockify/usuarios?email=${encodeURIComponent(email)}`)
    return Array.isArray(usuarios)
      ? usuarios.map((usuario) => ({
          id: usuario.id ?? usuario.Id ?? '',
          nome: usuario.nome ?? usuario.Nome ?? '',
          email: usuario.email ?? usuario.Email,
        }))
      : []
  },

  async getUsuarioById(id: string): Promise<UsuarioFormData> {
    const usuario = await api.get<any>(`/usuario/${id}`)
    return convertToFormData(usuario)
  },

  async criarUsuario(data: UsuarioFormData): Promise<Usuario> {
    const backendData = convertToBackend(data)
    const created = await api.post<any>('/usuario', backendData)
    return normalizeUsuario(created)
  },

  async atualizarUsuario(id: string, data: UsuarioFormData): Promise<Usuario> {
    const backendData = convertToBackend({ ...data, id })
    const updated = await api.put<any>(`/usuario/${id}`, backendData)
    return normalizeUsuario(updated)
  },

  async excluirUsuario(id: string): Promise<void> {
    await api.delete(`/usuario/${id}`)
  },

  async listarPessoasAtivas(): Promise<Pessoa[]> {
    // Cadastro de usuário pode vincular também pessoas de times externos.
    const pessoas = await api.get<any[]>('/Pessoa?apenasAtivos=true&incluirExternos=true')
    if (!Array.isArray(pessoas)) return []

    return pessoas.map((pessoa: any) => ({
      idPessoa: pessoa.idPessoa ?? pessoa.id ?? pessoa.IdPessoa ?? 0,
      id: String(pessoa.idPessoa ?? pessoa.id ?? pessoa.IdPessoa ?? 0),
      nome: pessoa.nome ?? pessoa.Nome ?? '',
      email: pessoa.email ?? pessoa.Email ?? '',
      telefone: pessoa.telefone ?? pessoa.Telefone ?? '',
      observacao: pessoa.observacao ?? pessoa.Observacao ?? '',
      createdAt: pessoa.dataCadastro ?? pessoa.DataCadastro ?? '',
      updatedAt: pessoa.dataUltimaAtualizacao ?? pessoa.DataUltimaAtualizacao ?? '',
    }))
  },

  async listarPapeis(): Promise<PapelDisponivel[]> {
    const papeis = await api.get<any[]>('/usuario/papeis')
    if (!Array.isArray(papeis)) return []

    return papeis.map((papel: any) => ({
      codigo: String(papel.codigo ?? papel.Codigo ?? ''),
      nome: papel.nome ?? papel.Nome ?? '',
      descricao: papel.descricao ?? papel.Descricao ?? '',
    }))
  },

  async uploadFoto(usuarioId: string, foto: File): Promise<void> {
    const formData = new FormData()
    formData.append('foto', foto)
    await api.post(`/usuario/${usuarioId}/foto`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  async atualizarPerfil(data: {
    nome: string
    email: string
    senhaAtual?: string
    novaSenha?: string
    foto?: File
  }): Promise<void> {
    const formData = new FormData()
    formData.append('nome', data.nome)
    formData.append('email', data.email)
    if (data.senhaAtual) formData.append('senhaAtual', data.senhaAtual)
    if (data.novaSenha) formData.append('novaSenha', data.novaSenha)
    if (data.foto) formData.append('foto', data.foto)

    await api.put('/usuario/me/perfil', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
}

